import { useMemo, useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { LutienApi } from '@src/services/lutien/lutien-api';
import { OrgConfigApi } from '@src/services/org-config/org-config-api';
import { AliareMiddlewareApi } from '@src/services/aliare-middleware/aliare-middleware-api';
import { AliareFieldMappingView } from './AliareFieldMappingView';
import { useExcelMappingUtils } from './hooks/useExcelMappingUtils';
import type {
  ConfigMapperDTO,
  CreateMappingsBodyDTO,
  CreateMappingItemDTO,
} from '@src/services/aliare-middleware/modules/v1/org-setup/dto';
import type { AliareFieldMapping } from '@src/services/org-config/dto';
import { AVAILABLE_ALIARE_FIELDS, FIXED_ALIARE_FIELDS } from '@src/services/org-config/dto';

export const AliareFieldMappingContainer = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get('organizationId');

  const lutienApi = useMemo(() => new LutienApi(), []);
  const orgConfigApi = useMemo(() => new OrgConfigApi(), []);
  const aliareMiddlewareApi = useMemo(() => new AliareMiddlewareApi(), []);
  const excelUtils = useExcelMappingUtils();

  const [mappings, setMappings] = useState<AliareFieldMapping[]>([]);
  const [saveSuccessOpen, setSaveSuccessOpen] = useState(false);
  const [saveErrorOpen, setSaveErrorOpen] = useState(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | undefined>(undefined);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { data: organizations, isLoading: isLoadingOrgs } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => lutienApi.v1.organizations.get(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: orgCredentials, isLoading: isLoadingCredentials } = useQuery({
    queryKey: ['org-config', organizationId],
    queryFn: async () => {
      if (!organizationId) return null;
      try {
        return await orgConfigApi.v1.credentials.get({ organizationId });
      } catch {
        return null;
      }
    },
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const {
    data: existingMappings,
    isLoading: isLoadingMappings,
    refetch: refetchMappings,
  } = useQuery({
    queryKey: ['aliare-mappings', organizationId],
    queryFn: () =>
      organizationId ? aliareMiddlewareApi.v1.orgSetup.getAllMappings(organizationId) : Promise.resolve([]),
    enabled: !!organizationId && !!orgCredentials && !isLoadingCredentials,
    staleTime: Infinity, // Nunca expira automaticamente - só recarrega manualmente
    gcTime: Infinity, // Mantém em cache permanentemente
  });

  const { data: customFields } = useQuery({
    queryKey: ['custom-fields', organizationId],
    queryFn: () =>
      organizationId ? aliareMiddlewareApi.v1.orgSetup.getCustomFields(organizationId) : Promise.resolve([]),
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000,
  });

  const customerCustomFields = useMemo(() => {
    return (customFields || []).filter((field) => field.entity.toLowerCase().includes('customer'));
  }, [customFields]);

  const saveMappingsMutation = useMutation({
    mutationFn: (body: CreateMappingsBodyDTO) => {
      if (!organizationId) throw new Error('Organization ID is required');
      return aliareMiddlewareApi.v1.orgSetup.replaceMappings(organizationId, body);
    },
    onSuccess: () => {
      setSaveSuccessOpen(true);
      setHasUnsavedChanges(false);
      refetchMappings();
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      setSaveErrorMessage(error.message || 'Erro ao salvar mapeamentos');
      setSaveErrorOpen(true);
    },
  });

  useEffect(() => {
    // Só inicializa se não há mudanças não salvas ou se é o primeiro carregamento
    if (existingMappings && (!hasUnsavedChanges || mappings.length === 0)) {
      initializeMappings(existingMappings);
      setHasUnsavedChanges(false);
    } else if (!isLoadingMappings && mappings.length === 0) {
      initializeMappings([]);
      setHasUnsavedChanges(false);
    }
  }, [existingMappings, isLoadingMappings, hasUnsavedChanges, mappings.length]);

  const initializeMappings = (existing: ConfigMapperDTO[]) => {
    // Combinar campos da API com campos disponíveis para garantir que todos sejam exibidos
    const allFields = new Set([...AVAILABLE_ALIARE_FIELDS, ...existing.map((m) => m.aliare_field)]);

    const newMappings: AliareFieldMapping[] = Array.from(allFields).map((aliareField) => {
      const existingMapping = existing.find((m) => m.aliare_field === aliareField);
      const isFixed = Object.prototype.hasOwnProperty.call(FIXED_ALIARE_FIELDS, aliareField);

      return {
        aliareField,
        customerUnitField: existingMapping?.tarken_field || '',
        customerGroupField: existingMapping?.group_field || '',
        transformer: existingMapping?.transformer ? JSON.stringify(existingMapping.transformer) : '',
        isFixedField: isFixed,
        fixedValue: isFixed ? FIXED_ALIARE_FIELDS[aliareField] : undefined,
        description: getFieldDescription(aliareField),
      };
    });

    const sortedMappings = newMappings.sort((a, b) => {
      if (a.isFixedField && !b.isFixedField) return -1;
      if (!a.isFixedField && b.isFixedField) return 1;
      return a.aliareField.localeCompare(b.aliareField);
    });

    setMappings(sortedMappings);
  };

  const getFieldDescription = (field: string): string => {
    const descriptions: Record<string, string> = {
      codigo: 'Código do Parceiro',
      pessoa: 'Código da Pessoa que Realizou o Cadastro',
      razaoSocial: 'Razão Social',
      nomeFantasia: 'Nome Fantasia',
      tipoPessoa: 'Tipo de Pessoa: Física ou Jurídica',
      cpfCnpj: 'CPF ou CNPJ',
      rgInscricaoEstadual: 'RG ou Inscrição Estadual',
      orgaoEmissor: 'Órgão Emissor do RG',
      inscricaoMunicipal: 'Inscrição Municipal',
      dataRgInscricaoEstadual: 'Data Emissão RG ou da Inscrição Estadual',
      sexo: 'Sexo do Parceiro',
      dataCadastro: 'Data de Cadastro no ERP',
      endereco: 'Endereço Principal',
      numero: 'Número do Endereço',
      bairro: 'Bairro do Endereço',
      cep: 'CEP do Endereço',
      municipio: 'Código do Município',
      telefone: 'Telefone',
      celular: 'Celular',
      fax: 'Fax',
      email: 'E-mail',
      cliente: 'Parceiro Cliente? Sim ou Não',
      fornecedor: 'Parceiro Fornecedor? Sim ou Não',
      transportador: 'Parceiro Transportador? Sim ou Não',
      longitude: 'Longitude',
      latitude: 'Latitude',
      dataInsercao: 'Data de Inserção no Banco de Dados',
      dataManutencao: 'Data da Última Alteração do Registro',
      dataSincronizacao: 'Data da Última Sincronização',
      tipoContribuinte: 'Tipo Contribuinte de ICMS: [1] Contribuinte | [2] Não contribuinte',
      site: 'Site',
      observacao: 'Observações Gerais do Cadastro',
      situacao: 'Situação do Cadastro: Ativo ou Inativo',
      idEstrangeiro: 'Número de Identificação de Parceiro Estrangeiro',
      funruralSENAR: 'Tipo de Tributação Funrural Senar',
      codigoCND: 'Código CND da Empresa',
      autenticacaoCND: 'Código da Autenticação da CND',
      validadeCND: 'Data de Validade da CND',
      tipoContribuinteIPI: 'Código do Tipo de Contribuinte de IPI',
      tipoContribuintePisCofins: 'Código do Tipo de Contribuinte de PIS/COFINS',
      emailVendedor: 'Consultor responsavel',
      gestorResponsavel: 'Gestor Responsável',
      filial: 'Filial',
    };
    return descriptions[field] || '';
  };

  const handleMappingChange = (
    aliareField: string,
    field: 'customerUnitField' | 'customerGroupField' | 'transformer',
    value: string,
  ) => {
    setMappings((prev) =>
      prev.map((mapping) => (mapping.aliareField === aliareField ? { ...mapping, [field]: value } : mapping)),
    );
    setHasUnsavedChanges(true);
  };

  const handleSave = () => {
    const mappingsToSave = mappings
      .filter((m) => (m.customerUnitField || '').trim() !== '')
      .map((m) => {
        const mapping = {
          aliare_field: m.aliareField,
          tarken_field: m.customerUnitField || '',
          group_field: m.customerGroupField || '',
        } as CreateMappingItemDTO;

        if ((m.transformer || '').trim() !== '') {
          try {
            mapping.transformer = JSON.parse(m.transformer);
          } catch {
            console.warn(`Invalid JSON transformer for field ${m.aliareField}:`, m.transformer);
          }
        }

        return mapping;
      });

    const payload = { mappings: mappingsToSave };

    saveMappingsMutation.mutate(payload);
  };

  const handleExportExcel = () => {
    const selectedOrg = organizations.find((org) => org.id === organizationId);
    const orgName = selectedOrg?.fantasyName || selectedOrg?.corporateName || 'N/A';

    const editableMappings = mappings.filter((m) => !m.isFixedField);
    excelUtils.exportToExcel(editableMappings, customerCustomFields, orgName);
  };

  const handleImportExcel = async (file: File) => {
    try {
      const importedMappings = await excelUtils.importFromExcel(file, customerCustomFields);

      const updatedMappings = mappings.map((existing) => {
        const imported = importedMappings.find((imp) => imp.aliareField === existing.aliareField);
        return imported || existing;
      });

      setMappings(updatedMappings);
      setSaveSuccessOpen(true);
    } catch (error) {
      setSaveErrorMessage(error instanceof Error ? error.message : 'Erro ao importar arquivo');
      setSaveErrorOpen(true);
    }
  };

  if (!organizationId) {
    return (
      <div>
        <p>Selecione uma organização para configurar os mapeamentos Aliare.</p>
        <button onClick={() => navigate('/org-config')}>Ir para Configuração de Organização</button>
      </div>
    );
  }

  if (!isLoadingCredentials && !orgCredentials) {
    return (
      <div style={{ padding: 16 }}>
        <p>
          Esta organização ainda não possui credenciais configuradas. Configure as credenciais antes de definir os
          mapeamentos.
        </p>
        <button onClick={() => navigate('/org-config')}>Configurar credenciais</button>
      </div>
    );
  }

  return (
    <AliareFieldMappingView
      organizations={organizations || []}
      isLoadingOrgs={isLoadingOrgs}
      organizationId={organizationId}
      mappings={mappings}
      customerCustomFields={customerCustomFields}
      isLoading={isLoadingMappings || isLoadingCredentials}
      isSaving={saveMappingsMutation.isPending}
      saveSuccessOpen={saveSuccessOpen}
      onCloseSaveSuccess={() => setSaveSuccessOpen(false)}
      saveErrorOpen={saveErrorOpen}
      saveErrorMessage={saveErrorMessage}
      onCloseSaveError={() => setSaveErrorOpen(false)}
      onMappingChange={handleMappingChange}
      onSave={handleSave}
      onExportExcel={handleExportExcel}
      onImportExcel={handleImportExcel}
      onBackToOrgConfig={() => navigate('/org-config')}
    />
  );
};
